import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import useOrderNotes from './hooks';
import AddNoteModal from './components/AddNoteModal';
import Note from './components/Note';
import { DeleteConfirmationModal } from 'components/delete-confirmation';

const useStyles = makeStyles((theme: any) =>
  createStyles({
    container: {
      padding: theme.spacing(1),
      margin: theme.spacing(1),
      backgroundColor: theme.palette.background.paperTranslucent,
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
        margin: theme.spacing(2),
      },
    },
    containerNotes: {
      display: 'grid',
      gridTemplateColumns: 'repeat(1, 1fr)',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'repeat(4, 1fr)',
      },
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(4),
      marginTop: theme.spacing(2),
      minHeight: '200px',
    },
    emptyStateContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(4),
      marginTop: theme.spacing(2),
      textAlign: 'center',
      minHeight: '300px',
    },
    emptyStateIcon: {
      fontSize: '3rem',
      color: theme.palette.grey[400],
      marginBottom: theme.spacing(2),
    },
  })
);

const Notes = () => {
  const classes = useStyles();
  const { t } = useTranslation('orders');
  const {
    notesOrder,
    openModal,
    openDelete,
    handleToogleDelete,
    handleToogleOpen,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    isNotesLoading,
    createNoteLoading,
    updateNoteLoading,
    deleteNoteLoading,
  } = useOrderNotes();

  return (
    <Paper className={classes.container}>
      <Header handleToogleOpen={handleToogleOpen} notes={notesOrder} />
      {openModal.open && (
        <AddNoteModal
          open={openModal.open}
          handleClose={handleToogleOpen}
          handleCreateNote={handleCreateNote}
          note={openModal.note}
          handleUpdateNote={handleUpdateNote}
          createNoteLoading={createNoteLoading}
          updateNoteLoading={updateNoteLoading}
        />
      )}
      {isNotesLoading && (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )}
      {!isNotesLoading && (!notesOrder || notesOrder.length === 0) && (
        <Box className={classes.emptyStateContainer}>
          <FontAwesomeIcon icon="sticky-note" className={classes.emptyStateIcon} />
          <Typography variant="h6" gutterBottom>
            {t('noNotesTitle')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('noNotesMessage')}
          </Typography>
        </Box>
      )}
      <Box className={classes.containerNotes}>
        {!isNotesLoading && notesOrder?.map((note) => (
          <Note
            key={note._id}
            note={note}
            handleToogleOpen={handleToogleOpen}
            handleToogleDelete={handleToogleDelete}
          />
        ))}
      </Box>
      {openDelete.status && (
        <DeleteConfirmationModal
          title={t('deleteNoteTitle')}
          confirmationMessage={t('deleteNoteSubtitle')}
          isOpen={openDelete.status}
          onConfirm={() => {
            handleDeleteNote(openDelete.note ?? '');
          }}
          onClose={handleToogleDelete}
          isRemoveLoading={deleteNoteLoading}
          buttonConfirmText={t('deleteNoteConfirmButton')}
        />
      )}
    </Paper>
  );
};

export default Notes;
